/**
 * The landing's language choice (owner 2026-10-26: "if the user picked /tr, that language is selected").
 *
 * vivideo.ai's app links carry `lang=<locale>` when the visitor chose a language on the landing (a /tr … page). English
 * is served at the landing's root to everyone, so it sends nothing. That choice becomes THE language here, before
 * anything reads one:
 *   - V2: `vivideo-settings` (the settings store's persisted language) and `i18nextLng` (the detector's cache);
 *   - V3: its explicit choice, `vivideo_v3_lang` (src/v3/i18n/choice.ts).
 * Then `lang` leaves the address. Without the param nothing changes: a first visit still takes the browser's language.
 */
const V2_SETTINGS_KEY = 'vivideo-settings';
const V2_DETECTOR_KEY = 'i18nextLng';
/** src/v3/i18n/choice.ts V3_LANG_KEY (a literal: V2's entry does not import V3). */
const V3_LANG_KEY = 'vivideo_v3_lang';
/** The landing's locales are BCP 47 (pt-br, zh-hans); old codes some stacks still send. */
const ALIASES: Readonly<Record<string, string>> = { nb: 'no', nn: 'no', iw: 'he', in: 'id', mo: 'ro' };

/** The `lang` of a query string as one of our language codes, or null (absent or not one we ship). */
export function landingLanguage(search: string, supported: readonly string[]): string | null {
  const raw = new URLSearchParams(search).get('lang');
  if (!raw) return null;
  const base = raw.trim().replace(/_/g, '-').split('-')[0].toLowerCase();
  const code = ALIASES[base] ?? base;
  return supported.includes(code) ? code : null;
}

/** Reads the page's `lang` once, saves it as the person's language (V2 and V3), drops it from the address. */
export function takeLandingLanguage(supported: readonly string[]): string | null {
  if (typeof window === 'undefined') return null;
  let code: string | null = null;
  try {
    code = landingLanguage(window.location.search, supported);
    const url = new URL(window.location.href);
    if (url.searchParams.has('lang')) {
      url.searchParams.delete('lang');
      window.history.replaceState(window.history.state, '', url.toString());
    }
  } catch {
    return code;
  }
  if (!code) return null;
  try {
    const raw = localStorage.getItem(V2_SETTINGS_KEY);
    const stored = raw ? (JSON.parse(raw) as { state?: Record<string, unknown>; version?: number } | null) : null;
    const next = stored && typeof stored === 'object' ? { ...stored, state: { ...(stored.state ?? {}), language: code } } : { state: { language: code }, version: 1 };
    localStorage.setItem(V2_SETTINGS_KEY, JSON.stringify(next));
    localStorage.setItem(V2_DETECTOR_KEY, code);
    localStorage.setItem(V3_LANG_KEY, code);
  } catch {
    /* storage blocked: this page still opens in the chosen language (the caller passes it to i18next) */
  }
  return code;
}
